import { convertStoreCheckoutOrderUntilToTimelineCustomDate } from './storeCheckoutDate.utils';
import type { ICartItemDetail, ITimelineElement } from '@/common/interfaces/cart.interface';
import type { GetTimelineMutationFunction } from '@/common/interfaces/order.interface';
import { formatDeliveryDateRange } from '@/utilities/ShippingUtils';
import { extractAndEnrichTimeline } from '@/utilities/OrderUtils';

export interface IStoreCheckoutShippingTimelinePayload {
  fetchMaxTimeline: true;
  proofItemData: Array<{ proofItemId: number }>;
  customDate?: string;
}

export const collectStoreCheckoutProofItemIds = (items: ICartItemDetail[]): number[] => [
  ...new Set(
    items
      .map((item) => item.proofItemId)
      .filter((proofItemId): proofItemId is number => Number(proofItemId) > 0)
  ),
];

export const buildStoreCheckoutShippingTimelinePayload = (
  proofItemIds: number[],
  orderUntil?: string
): IStoreCheckoutShippingTimelinePayload => {
  const payload: IStoreCheckoutShippingTimelinePayload = {
    fetchMaxTimeline: true,
    proofItemData: proofItemIds.map((proofItemId) => ({ proofItemId })),
  };

  if (orderUntil?.trim()) {
    const customDate = convertStoreCheckoutOrderUntilToTimelineCustomDate(orderUntil.trim());
    if (customDate) {
      payload.customDate = customDate;
    }
  }

  return payload;
};

export const fetchStoreCheckoutShippingTimeline = async (
  getTimeline: GetTimelineMutationFunction,
  proofItemIds: number[],
  orderUntil?: string
): Promise<ITimelineElement[]> => {
  if (proofItemIds.length === 0) {
    return [];
  }

  try {
    const response = await getTimeline({
      data: buildStoreCheckoutShippingTimelinePayload(proofItemIds, orderUntil),
    });

    if (response && 'data' in response) {
      return extractAndEnrichTimeline(
        response.data as Parameters<typeof extractAndEnrichTimeline>[0]
      );
    }
  } catch {
    return [];
  }

  return [];
};

const normalizeStoreCheckoutShipType = (shipType: string): string =>
  (shipType || 'standard').toLowerCase();

const findStoreCheckoutTimelineElement = (
  timelineData: ITimelineElement[],
  shipTypeKey: string
): ITimelineElement | undefined =>
  timelineData.find(
    (element) =>
      normalizeStoreCheckoutShipType(element.type) === normalizeStoreCheckoutShipType(shipTypeKey)
  );

export { findStoreCheckoutTimelineElement };

export const resolveStoreCheckoutEstDeliveryByRange = (
  timelineData: ITimelineElement[],
  shipTypeKey: string
): string => {
  const timelineElement = findStoreCheckoutTimelineElement(timelineData, shipTypeKey);

  if (!timelineElement) {
    return '';
  }

  const minIso = timelineElement.newTimeline?.minDate?.dateInISOString;
  const maxIso = timelineElement.newTimeline?.maxDate?.dateInISOString;

  if (minIso && maxIso) {
    const formattedRange = formatDeliveryDateRange(minIso, maxIso);
    if (formattedRange) {
      return formattedRange;
    }
  }

  const fallbackIso = timelineElement.estDeliveryDate?.dateInISOString;
  if (fallbackIso) {
    return formatDeliveryDateRange(fallbackIso, fallbackIso);
  }

  return '';
};

export const resolveStoreCheckoutSummaryDeliveryDisplay = (
  orderUntil: string,
  shipTypeKey: string,
  timelineData: ITimelineElement[]
): string => {
  if (!orderUntil.trim() || !shipTypeKey.trim()) {
    return 'TBD';
  }

  return resolveStoreCheckoutEstDeliveryByRange(timelineData, shipTypeKey) || 'TBD';
};
